import { createContext, useContext, useState, useMemo, useEffect } from 'react';

import { SabaCoinEventInfo } from '@/types';

import { useReward } from '@/contexts/reward-context';

interface SabaCoinEventContextType {
	sabaCoinEventInfo: SabaCoinEventInfo;
	setSabaCoinEventInfo: React.Dispatch<React.SetStateAction<SabaCoinEventInfo>>;
	fetchSabaCoinEventData: () => Promise<void>;
}

const SabaCoinEventContext = createContext<SabaCoinEventContextType | undefined>(undefined);

export const SabaCoinEventContextProvider = ({
	children
}: {
	children: React.ReactNode
}) => {
	const { getSabaCoinEventInfo } = useReward();
	const [sabaCoinEventInfo, setSabaCoinEventInfo] = useState<SabaCoinEventInfo>({
		WeeklyBettingAmount: 0,
		WeeklyRedeemedAmount: 0,
		WeeklyResetDate: '',
		Thresholds: [],
	});

	const fetchSabaCoinEventData = async () => {
		if (!getSabaCoinEventInfo) return;

		try {
			const eventData = await getSabaCoinEventInfo();
			setSabaCoinEventInfo(eventData);
		} catch (error) {
			console.error('getSabaCoinEventInfo error', error);
		}
	};

	useEffect(() => {
		fetchSabaCoinEventData();
	}, [getSabaCoinEventInfo]);

	const sabaCoinEventInfoValue = useMemo(() => ({ sabaCoinEventInfo, setSabaCoinEventInfo }), [sabaCoinEventInfo]);

	if (!getSabaCoinEventInfo || !sabaCoinEventInfo) {
		return null;
	}
	
	const value = {
		...sabaCoinEventInfoValue,
		fetchSabaCoinEventData
	};

	return (
		<SabaCoinEventContext.Provider value={value}>
			{children}
		</SabaCoinEventContext.Provider>
	);
};

export const useSabaCoinEvent = () => {
	const context = useContext(SabaCoinEventContext);

	if (context === undefined) {
		throw new Error('useSabaCoinEvent must be used within a SabaCoinEventContextProvider');
	}

	return context;
};